import { Auth } from './app/auth'
import {
  MapFileTypes,
  MS_FILE_EXT_DOC,
  MS_FILE_EXT_DOCX,
  MS_FILE_EXT_EXCEL,
  MS_FILE_EXT_EXCELX,
  MS_FILE_EXT_PPT,
  MS_FILE_EXT_PPTX,
  MS_PDF,
  MS_VSDX,
  MS_VSD,
  MS_ODT,
  MS_ODS,
  MS_ODP,
  MS_TXT,
  MS_RTF,
  MS_PNG,
  MS_JPEG,
  MS_HTML,
  MS_CSV,
  MS_MP4,
  MS_GIF,
  MS_JPG,
  MS_JPE,
  IS_QUICK_ACCESS_ENABLED,
  MS_COMMAND_AUTHENTICATE,
} from './constants'

/**
 * Extracts the file type from a file name. Throws an error if file name does not have the
 * file extension or if the file type is unsupported
 * @param {String} fileName File name in the format of {file name}.{file extension}
 * @returns File Type for Connectors API. Refer to extensionToType for possible values
 */
export const extractFileTypeFromFileName = (fileName: string) => {
  const splitByPeriod = fileName.split('.')
  const splitLength = splitByPeriod.length
  if (splitLength === 0) {
    // defensive check in case there is no file extension
    throw new Error('File Extension Not Found')
  }
  const fileExtension = splitByPeriod[splitLength - 1]
  const fileType = (MapFileTypes as Record<string, string>)[fileExtension]
  if (!fileType) {
    // defensive check in case the file extension is not supported
    throw new Error('File Type Unsupported')
  }
  return fileType
}

export const getSupportedFileExtensionsArr = () => {
  const extensions = [
    MS_FILE_EXT_DOC,
    MS_FILE_EXT_DOCX,
    MS_FILE_EXT_EXCEL,
    MS_FILE_EXT_EXCELX,
    MS_FILE_EXT_PPT,
    MS_FILE_EXT_PPTX,
    MS_PDF,
    MS_VSDX,
    MS_VSD,
    MS_ODT,
    MS_ODS,
    MS_ODP,
    MS_TXT,
    MS_RTF,
    MS_PNG,
    MS_JPEG,
    MS_HTML,
    MS_CSV,
    MS_MP4,
    MS_GIF,
    MS_JPG,
    MS_JPE,
  ]
  const res = extensions.map((ext) => `.${ext}`)
  return res
}

const filePickerConfig = (
  channelId: string,
  origin: string,
  _isDarkMode: boolean,
  isOneDrive = true
) => ({
  sdk: '8.0',
  entry: {
    [isOneDrive ? 'oneDrive' : 'sharePoint']: {
      files: {},
    },
  },
  search: {
    enabled: true,
  },
  authentication: {},
  messaging: {
    origin,
    channelId,
  },
  typesAndSources: {
    mode: 'files',
    pivots: {
      oneDrive: true,
      recent: true,
      // show Quick Access tabs: this will show/hide access to Sharepoint
      sharedLibraries: IS_QUICK_ACCESS_ENABLED,
    },
    filters: getSupportedFileExtensionsArr(),
  },
  selection: {
    mode: 'single',
  },
  theme: 'default',
})

/**
 * Uses connector's auth status response to send appropriate access token to OneDrive
 */
export const getToken = (
  command: { command: string; resource: string },
  authObj: Auth
) => {
  const { resource = {} } = authObj.extra
  const { resource: resourceUrl } = command
  return resource[removeTrailingSlash(resourceUrl)]
}

export const getFilePickerQueryString = (
  channelId: string,
  origin: string,
  isDarkMode: boolean,
  isOneDrive: boolean
) => {
  return new URLSearchParams({
    filePicker: JSON.stringify(
      filePickerConfig(channelId, origin, isDarkMode, isOneDrive)
    ),
  })
}
/**
 * Connector's auth status returns a map of url to access token. The url key may or may not have a trailing /.
 * This functions ensures that the url does *not* have a trailing slash
 */
export const generateResourceMap = (resourceObj: Record<string, string>) => {
  const resourceUrls = Object.keys(resourceObj)
  const resourceMap: Record<string, string> = {}
  for (const unformattedUrl of resourceUrls) {
    const formattedUrl = removeTrailingSlash(unformattedUrl)
    resourceMap[formattedUrl] = resourceObj[unformattedUrl]
  }
  return resourceMap
}

const removeTrailingSlash = (str: string) => {
  return str[str.length - 1] === '/' ? str.slice(0, -1) : str
}

export const createFormInWindow = (
  window: Window,
  queryString: string,
  authObj: Auth
) => {
  const baseUrl = authObj.extra.baseUrl!
  const url = baseUrl + `/_layouts/15/FilePicker.aspx?${queryString}`
  const accessToken = getToken(
    {
      resource: baseUrl,
      command: MS_COMMAND_AUTHENTICATE,
    },
    authObj
  )
  const form = window.document.createElement('form')
  form.setAttribute('method', 'POST')
  form.setAttribute('action', url) // what is the url for?
  window.document.body.append(form)

  const input = window.document.createElement('input')
  input.setAttribute('type', 'hidden')
  input.setAttribute('name', 'access_token')
  input.setAttribute('value', accessToken)
  form.appendChild(input)
  return form
}

export function generateZoomCollabFilename(): string {
  const now = new Date()

  const formattedDate = now.toLocaleDateString('en-CA') // YYYY-MM-DD format
  const formattedTime = now
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
    .replace(/:/g, '.')

  return `Zoom Collaboration ${formattedDate} at ${formattedTime}`
}

// Helper function to check if a value is an object (and not null)
export const isObject = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object'
}

export const obtainErrorInfo = (e: unknown) => {
  let message = 'unknown error'
  let status = 500
  let error: unknown
  if (isObject(e)) {
    if (isObject(e.payload)) {
      error = e.payload
    } else if (isObject(e.error)) {
      error = e.error
    } else if (isObject(e.data)) {
      error = e.data
    }
  } else if (typeof e === 'string') {
    message = e
  }

  if (isObject(error)) {
    if ('data' in error && isObject(error.data) && 'message' in error.data) {
      message = (error.data as { message: string }).message
    } else if ('message' in error) {
      message = error.message as string
    }

    if ('status' in error) {
      status = error.status as number
    }
  }

  return { message, status }
}
